import React, { useState } from 'react';
import { apiOrigin } from '../utils/surface';

type EntryPhoto = {
  id: string;
  visitor_name: string;
  device_name?: string | null;
  device_id: string;
  created_at: string;
  unlock_succeeded: boolean;
};

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const VisitorPassEntryPhotos: React.FC<{ propertyId: string }> = ({ propertyId }) => {
  const [photos, setPhotos] = useState<EntryPhoto[] | null>(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true); setError('');
    try {
      const response = await fetch(`${apiOrigin()}/visitors/entry-photos?property_id=${encodeURIComponent(propertyId)}`, { headers: authHeaders() });
      if (!response.ok) throw new Error(`Entry photos could not be loaded (${response.status})`);
      const result = await response.json();
      setPhotos(Array.isArray(result) ? result : []);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Entry photos could not be loaded'); }
    finally { setBusy(false); }
  };

  const view = async (id: string) => {
    setBusy(true); setError(''); setPreview('');
    try {
      const response = await fetch(`${apiOrigin()}/visitors/entry-photos/${encodeURIComponent(id)}`, { headers: authHeaders(), cache: 'no-store' });
      if (!response.ok) throw new Error(`Entry photo could not be opened (${response.status})`);
      const result = await response.json();
      if (!result.image_base64 || !['image/jpeg', 'image/png', 'image/webp'].includes(result.mime_type)) throw new Error('Invalid entry photo response');
      setPreview(`data:${result.mime_type};base64,${result.image_base64}`);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Entry photo could not be opened'); }
    finally { setBusy(false); }
  };

  return <div className="border border-gray-300 bg-white p-3 text-sm">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h4 className="font-semibold text-gray-900">Visitor-pass entry photos</h4><p className="text-xs text-gray-600">Encrypted photos linked to recent access attempts.</p></div>
      <button type="button" className="border border-gray-400 px-3 py-2 font-semibold text-gray-800 disabled:opacity-50" disabled={busy} onClick={load}>{busy ? 'Loading…' : 'View recent photos'}</button>
    </div>
    {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
    {photos && <div className="mt-3 max-h-64 overflow-y-auto border-t border-gray-300">
      {photos.length === 0 ? <p className="py-3 text-gray-600">No entry photos recorded.</p> : photos.map(photo => <div key={photo.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 py-2">
        <span><b>{photo.visitor_name}</b> · {photo.device_name || photo.device_id} · {new Date(photo.created_at).toLocaleString()} · {photo.unlock_succeeded ? 'Unlocked' : 'Attempted'}</span>
        <button type="button" className="border border-gray-400 px-2 py-1 text-xs font-semibold" disabled={busy} onClick={() => view(photo.id)}>View photo</button>
      </div>)}
    </div>}
    {preview && <div className="mt-3"><img src={preview} alt="Visitor pass entry" className="max-h-80 max-w-full border border-gray-300 object-contain"/><button type="button" className="mt-2 border border-gray-400 px-3 py-1 text-xs" onClick={() => setPreview('')}>Close photo</button></div>}
  </div>;
};
